/**
 * Renders an artifact's body content with the right viewer for the
 * mime type, plus a header row (Content label on left, mode toggle +
 * external-link + download + optional Copy on the right).
 *
 * Mode toggle pref ("text vs. native") is persisted in localStorage so
 * the user's last choice is remembered across artifacts and kinds.
 *
 * Modes per content type:
 *   email with body_html         → Text (default) | HTML
 *   .docx with cached_file       → Text (default) | Rendered (mammoth.js)
 *   .pdf with cached_file        → Text (default) | PDF (embed)
 *   .html file (text/html)       → Text (default) | HTML (sanitized)
 *   image                        → inline always (no toggle)
 *   plain text                   → text only
 *   Google native (no content)   → external "Open in Google …" link
 *   other binary fallback        → download link only
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import DOMPurify from 'dompurify'
import { Download, ExternalLink, Copy, Check } from 'lucide-react'
import { useAuth } from '../auth'
import { isPlayheadTenant } from '../playhead'

type Props = {
  mime: string
  cachedContent: string
  bodyHtml?: string
  cachedFileUrl: string | null
  cachedFileSize?: number | null
  storageMode: string
  /** Source system identifier — used to construct external "open in Google
   *  Drive / Docs / Sheets / Slides" links for gdrive-sourced artifacts
   *  whose native format isn't downloadable as a file. */
  sourceType?: string
  sourceExternalId?: string
  /** Height for embed/iframe. Number → pixels. String → any valid CSS length. */
  pdfHeight?: number | string
  /** Header label shown to the left of the toggle. Defaults to "Content". */
  headerLabel?: string
  /** Optional copy callback. When provided, a "Copy" button appears
   *  rightmost in the header row. */
  onCopy?: () => void
  /** Briefly true after a successful copy; flips the button to "Copied". */
  copied?: boolean
  /** Buttons rendered on the RIGHT of the toolbar (e.g. Pull). */
  headerActions?: ReactNode
  /** Buttons rendered in the MIDDLE of the toolbar, next to Copy (e.g. View). */
  centerActions?: ReactNode
  /** Suppress the docx "Rendered" (mammoth) toggle. Set for marked-up
   *  attachments (tracked changes + comments): mammoth renders the raw
   *  revisions inline — the messy view we retired in favor of the clean
   *  markup modal. Pins the docx to its extracted Text view. */
  suppressNativeToggle?: boolean
  /** Artifact type ('email' | 'attachment' | ...). With sourceType it decides
   *  synthetic KEN/DEMO rendering (tracked changes on attachments). */
  artifactType?: string
}

type Mode = 'text' | 'native'
type Kind = 'image' | 'pdf' | 'docx' | 'html' | 'htmlfile' | 'text' | 'binary'

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const PREF_KEY = 'me.bodypreview.preferText'

/** Map a Google-native mime + Drive file_id to a "open in Google …" URL.
 *  Returns null for non-Google or non-renderable mimes. */
function googleAppUrl(mime: string, externalId: string | undefined | null): { url: string; label: string } | null {
  if (!externalId) return null
  switch ((mime || '').toLowerCase()) {
    case 'application/vnd.google-apps.document':
      return { url: `https://docs.google.com/document/d/${externalId}/edit`, label: 'Open in Docs' }
    case 'application/vnd.google-apps.spreadsheet':
      return { url: `https://docs.google.com/spreadsheets/d/${externalId}/edit`, label: 'Open in Sheets' }
    case 'application/vnd.google-apps.presentation':
      return { url: `https://docs.google.com/presentation/d/${externalId}/edit`, label: 'Open in Slides' }
    case 'application/vnd.google-apps.form':
      return { url: `https://docs.google.com/forms/d/${externalId}/edit`, label: 'Open in Forms' }
    case 'application/vnd.google-apps.folder':
      return { url: `https://drive.google.com/drive/folders/${externalId}`, label: 'Open in Drive' }
    default:
      return null
  }
}

function getPreferText(): boolean {
  try {
    const v = localStorage.getItem(PREF_KEY)
    return v === null ? true : v === '1'
  } catch { return true }
}
function setPreferText(v: boolean) {
  try { localStorage.setItem(PREF_KEY, v ? '1' : '0') } catch { /* ignore */ }
}

function prettySize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function detectKind(mime: string, hasFileUrl: boolean, hasBodyHtml: boolean, hasText: boolean): Kind {
  const m = (mime || '').toLowerCase()
  if (hasFileUrl && m.startsWith('image/')) return 'image'
  if (hasFileUrl && m === 'application/pdf') return 'pdf'
  if (hasFileUrl && m === DOCX_MIME) return 'docx'
  // text/html artifacts (e.g. SecureMessageAtt.html attachments, exported
  // appeal docs): cached_content IS the html source, so we render that
  // sanitized for the native view and the same string for the text view.
  if (m === 'text/html' && hasText) return 'htmlfile'
  if (hasBodyHtml) return 'html'
  if (hasText) return 'text'
  if (hasFileUrl) return 'binary'
  return 'text'
}

function nativeLabelFor(kind: Kind): string {
  switch (kind) {
    case 'pdf': return 'PDF'
    case 'docx': return 'Rendered'
    case 'html': return 'HTML'
    case 'htmlfile': return 'HTML'
    default: return 'Native'
  }
}

export default function BodyPreview({
  mime, cachedContent, bodyHtml, cachedFileUrl, cachedFileSize, storageMode,
  sourceType, sourceExternalId,
  pdfHeight = 700, onCopy, copied = false,
  suppressNativeToggle = false, headerActions, centerActions, artifactType,
}: Props) {
  const hasText = !!(cachedContent && cachedContent.length)
  const hasBodyHtml = !!(bodyHtml && bodyHtml.trim())
  const hasFileUrl = !!cachedFileUrl
  const kind = detectKind(mime, hasFileUrl, hasBodyHtml, hasText)
  // Synthetic KEN/DEMO content (ken_demo / ken_deid): render pane-3's HTML view
  // from the Body text (cached_content) so it always reflects the pulled Notion
  // Body — never a stale original email/doc (Ken T3 #6). Attachments also get
  // tracked-change rendering ([del]{ins}) (Ken T3 #9). Text view stays raw.
  const synthetic = sourceType === 'ken_demo' || sourceType === 'ken_deid'
  const synthHtml = synthetic && hasText
  const trackChanges = synthetic && artifactType === 'attachment'
  const nativeLabel = synthHtml ? 'HTML' : nativeLabelFor(kind)
  const supportsToggle =
    synthHtml ||
    (kind === 'pdf' && hasText) ||
    // Marked-up docx hides its mammoth "Rendered" view (see suppressNativeToggle).
    (kind === 'docx' && hasText && !suppressNativeToggle) ||
    (kind === 'html' && hasText) ||
    (kind === 'htmlfile' && hasText)
  // For gdrive-sourced Google-native artifacts (Sheets, Docs, Slides,
  // Forms, Folders), construct a deep link into the Google web UI. We
  // don't host the file; the link is the only way to view content for
  // mimes whose text wasn't extracted (Sheets) and a useful "go to
  // source" alternative for the rest.
  const externalLink = sourceType === 'gdrive' ? googleAppUrl(mime, sourceExternalId) : null

  // Pick a starting mode for this artifact: image/binary sit on 'native',
  // plain text on 'text', and toggleable kinds (pdf/docx/html with text)
  // honor the localStorage pref.
  const computeInitial = (): Mode => {
    if (kind === 'image' || kind === 'binary') return 'native'
    if (kind === 'text') return 'text'
    if (!hasText) return 'native'
    // Marked-up docx: pin to the clean Text view; the mammoth render is hidden.
    if (suppressNativeToggle && kind === 'docx') return 'text'
    return getPreferText() ? 'text' : 'native'
  }
  const [mode, setMode] = useState<Mode>(computeInitial)
  // Quiet pane-3 (Ken 2026-10-22, KEN/playhead): drop the Text/HTML toggle to the
  // same low weight as View/Pull so the four controls read as one quiet row
  // rather than competing.
  const bpAuth = useAuth()
  const bpQuiet = bpAuth.status === 'authenticated' && isPlayheadTenant(bpAuth.user.client?.code)

  // When the parent swaps in an artifact of a different kind (or the
  // text-availability flips), recompute mode from the pref so the new
  // artifact respects the user's standing preference.
  useEffect(() => {
    setMode(computeInitial())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, hasText, suppressNativeToggle])

  const onModeChange = (m: Mode) => {
    setMode(m)
    setPreferText(m === 'text')
  }

  return (
    <div>
      {/* Toolbar (Ken 2026-08-10): 3 zones — the single Text/HTML toggle is
          left-justified; Pull sits right-justified (via headerActions); Copy
          (icon only) + View (via centerActions) sit in between. Format
          utilities (open-in-Google, download) ride with the toggle on the
          left. */}
      <div className={`flex items-center justify-between gap-2 ${bpQuiet ? 'mb-4' : 'mb-1'}`}>
        {/* LEFT — one button that switches between Text and the native view. */}
        <div className="flex items-center gap-1.5 min-w-0">
          {supportsToggle && (
            <button
              onClick={() => onModeChange(mode === 'text' ? 'native' : 'text')}
              className={`me-idfont me-doc-toggle inline-flex items-center gap-1 rounded-sm px-2 py-0.5 hover:bg-[color:var(--me-panel)] ${bpQuiet ? 'text-[10px]' : 'text-[12.5px]'}`}
              title="Switch between extracted text and the native view"
            >
              <span className={mode === 'text' ? (bpQuiet ? 'me-text font-medium' : 'me-text font-semibold') : 'me-text-mute'}>Text</span>
              <span className="me-text-mute">/</span>
              <span className={mode !== 'text' ? (bpQuiet ? 'me-text font-medium' : 'me-text font-semibold') : 'me-text-mute'}>{nativeLabel}</span>
            </button>
          )}
          {externalLink && (
            <a
              href={externalLink.url}
              target="_blank"
              rel="noreferrer"
              className="me-btn me-btn-outline"
              title="Open the original Google file in a new tab"
            >
              <ExternalLink size={11} /> {externalLink.label}
            </a>
          )}
          {hasFileUrl && (
            <a
              href={cachedFileUrl!}
              download
              className="me-btn me-btn-outline"
              title={`download${cachedFileSize ? ` (${prettySize(cachedFileSize)})` : ''}`}
            >
              <Download size={11} /> download
            </a>
          )}
        </div>
        {/* CENTER — Copy (icon only) + View. gap-4 so Copy / Reply in Gmail /
            View aren't cramped together (Ken 2026-08-19). */}
        <div className="flex items-center gap-4 shrink-0">
          {/* Copy icon hidden on KEN/playhead — it moves behind the pane-3
              Manage menu (Ken 2026-10-22 B). Still shown for other tenants. */}
          {onCopy && !bpQuiet && (
            <button
              onClick={onCopy}
              className="self-center me-text-mute hover:me-text inline-flex items-center"
              title={copied ? 'Copied' : 'Copy a formatted version (header + body + comments + redlines) to clipboard'}
              aria-label={copied ? 'Copied' : 'Copy'}
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
            </button>
          )}
          {centerActions}
        </div>
        {/* RIGHT — Pull. */}
        <div className="flex items-center gap-1.5 shrink-0">
          {headerActions}
        </div>
      </div>

      {/* data-wp-body: the body-text region only (excludes the toolbar
          row above). Work Product selection-anchor capture computes
          prefix/suffix against this element, so context isn't polluted
          by the Content/Text/HTML/Copy header labels. */}
      <div data-wp-body>
      {kind === 'image' ? (
        <div className="rounded-md bg-[color:var(--me-panel)] p-3 text-center">
          <img src={cachedFileUrl!} alt="artifact" className="mx-auto max-h-[600px] rounded-sm" />
        </div>
      ) : kind === 'binary' ? (
        <div className="rounded-md bg-[color:var(--me-panel)] p-4 text-[12px] me-text-mute italic">
          Binary content of type <code className="me-mono">{mime || 'unknown'}</code> — no inline preview.{' '}
          <a href={cachedFileUrl!} target="_blank" rel="noreferrer" className="underline">Download</a>.
        </div>
      ) : mode === 'text' ? (
        hasText ? (
          <TextBlock text={cachedContent} quiet={bpQuiet} trackChanges={trackChanges} />
        ) : (
          <NoContentBlock mime={mime} storageMode={storageMode} externalLink={externalLink} />
        )
      ) : synthHtml ? (
        <SyntheticHtmlBody text={cachedContent} trackChanges={trackChanges} quiet={bpQuiet} />
      ) : kind === 'html' ? (
        <HtmlBody html={bodyHtml || ''} quiet={bpQuiet} />
      ) : kind === 'htmlfile' ? (
        <HtmlBody html={cachedContent} quiet={bpQuiet} />
      ) : kind === 'pdf' ? (
        <PdfBody url={cachedFileUrl!} pdfHeight={pdfHeight} />
      ) : kind === 'docx' ? (
        <DocxBody url={cachedFileUrl!} />
      ) : hasText ? (
        <TextBlock text={cachedContent} trackChanges={trackChanges} />
      ) : (
        <NoContentBlock mime={mime} storageMode={storageMode} externalLink={externalLink} />
      )}
      </div>
    </div>
  )
}

/** Friendly fallback when there's nothing inline to show. Surfaces the
 *  external Google link (if any) prominently — otherwise a small
 *  "no content" notice. */
function NoContentBlock({ mime, storageMode, externalLink }: {
  mime: string
  storageMode: string
  externalLink: { url: string; label: string } | null
}) {
  if (externalLink) {
    return (
      <div className="rounded-md bg-[color:var(--me-panel)] p-4 text-[13px] me-text-dim">
        Inline content not extracted for <code className="me-mono">{mime || 'unknown'}</code>.
        <div className="mt-2">
          <a
            href={externalLink.url}
            target="_blank"
            rel="noreferrer"
            className="me-btn me-btn-primary inline-flex items-center gap-1"
          >
            <ExternalLink size={12} /> {externalLink.label}
          </a>
        </div>
      </div>
    )
  }
  return (
    <div className="text-[12px] me-text-mute italic">
      No cached content. (Storage mode: {storageMode}.)
    </div>
  )
}

// Ken 2026-07-03: Drive's HTML export inlines every image as a
// markdown `![alt](data:image/png;base64,…)` URL. For a real doc
// that's tens of KB of unreadable base64 per image, interleaved with
// the actual text. Strip them down to a plain `[image: alt]`
// placeholder before rendering — the extracted text becomes legible.
// The full extracted content stays intact server-side in
// cached_content; this is display-only clean-up.
function stripInlineBase64Images(text: string): string {
  if (!text) return text
  // Markdown image with a data: URL. Alt text is optional; when
  // present, surface it in the placeholder so the reader knows what
  // was there. Handles both !\[alt](data:…) and !\[](data:…).
  return text.replace(
    /!\[([^\]]*)\]\(data:image\/[a-z+]+;base64,[a-zA-Z0-9+/=]+\)/gi,
    (_full, alt) => alt ? `[image: ${alt.trim()}]` : '[image]',
  )
}

function TextBlock({ text, quiet = false, trackChanges = false }: { text: string; quiet?: boolean; trackChanges?: boolean }) {
  const cleaned = stripInlineBase64Images(text)
  // T9 #7: tracked-change attachments (synthetic docx) carry [del]{ins} notation
  // in the Body text. The Text view used to show the raw brackets (e.g.
  // "[semester]{term}"); now it renders them the same way the HTML view does —
  // [text] = deletion (red strikethrough), {text} = insertion (blue underline),
  // brackets stripped. The <pre> preserves whitespace, so newlines stay as-is
  // (no <br> needed). Source is HTML-escaped first, so only our own span tags
  // reach the DOM (XSS-safe). Non-tracked text renders verbatim as before.
  const trackedHtml = useMemo(() => {
    if (!trackChanges) return null
    return escapeHtml(cleaned)
      .replace(/\[([^\][{}]*)\]/g, '<span style="color:#b4231a;text-decoration:line-through">$1</span>')
      .replace(/\{([^\][{}]*)\}/g, '<span style="color:#1a56b4;text-decoration:underline">$1</span>')
  }, [cleaned, trackChanges])
  // KEN/playhead reading body: 16px / 1.7 line-height (Ken 2026-10-23). MOVU keeps 12.5px.
  const cls = `me-connected me-record-body whitespace-pre-wrap break-words [overflow-wrap:anywhere] rounded-md p-4 me-text ${quiet ? 'leading-[1.7]' : 'leading-relaxed'}`
  const style = {
    fontSize: quiet ? 'var(--me-font-size-ground-1, 16px)' : 'var(--me-font-size-ground-1, 12.5px)',
    background: 'var(--me-font-record-body-bg, var(--me-panel))',
    color: 'var(--me-font-ground-3-color, var(--me-text))',
  } as const
  if (trackedHtml != null) {
    return <pre className={cls} style={style} dangerouslySetInnerHTML={{ __html: trackedHtml }} />
  }
  return (
    <pre className={cls} style={style}>
      {cleaned}
    </pre>
  )
}

function HtmlBody({ html, quiet = false }: { html: string; quiet?: boolean }) {
  // Sanitize: drop scripts, event handlers, embeds. Keep links + images +
  // styles + tables (legitimate email/doc content).
  const sanitized = useMemo(() => DOMPurify.sanitize(html, {
    FORBID_TAGS: ['script', 'object', 'embed', 'iframe', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  }), [html])
  return (
    <div
      className={`rounded-md bg-white p-4 text-black overflow-x-auto ${quiet ? 'leading-[1.7]' : 'leading-relaxed'}`}
      // UXT-2: body size follows --me-font-size-ground-1 (KEN 15px) so HTML emails
      // honour the reading base; MOVU keeps its old px via the fallback.
      style={{ fontSize: quiet ? 'var(--me-font-size-ground-1, 16px)' : 'var(--me-font-size-ground-1, 13px)' }}
      // Sanitized via DOMPurify above; no script/event-handler attrs survive.
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  )
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/** Synthetic KEN/DEMO HTML view, rendered from the Body text (Ken T3 #6, #9).
 *  Blank lines in the Body become paragraph spacing; single newlines become
 *  <br>. For attachments (trackChanges), tracked-change notation renders as
 *  track changes: [text] = deletion (red strikethrough), {text} = insertion
 *  (underline, colored); the brackets are stripped. Source text is HTML-escaped
 *  first, so only our own span/paragraph tags reach the DOM (XSS-safe). */
function SyntheticHtmlBody({ text, trackChanges, quiet = false }: {
  text: string; trackChanges: boolean; quiet?: boolean
}) {
  const html = useMemo(() => {
    return (text || '').split(/\n[ \t]*\n/).map((para) => {
      // Two-column block (Ken T6 #5c): a paragraph whose non-empty lines each
      // split on a run of 2+ spaces into exactly a left/right pair — e.g. the
      // two-party signature block. Render as side-by-side columns instead of one
      // run-on line (HTML would otherwise collapse the aligning spaces).
      const lines = para.split('\n')
      const nonEmpty = lines.filter((l) => l.trim())
      const pairs = nonEmpty.map((l) => { const mm = l.match(/^(.+?)\s{2,}(.+)$/); return mm ? [mm[1], mm[2]] : null })
      if (nonEmpty.length >= 2 && pairs.every(Boolean)) {
        const left = (pairs as string[][]).map((p) => escapeHtml(p[0].trim())).join('<br>')
        const right = (pairs as string[][]).map((p) => escapeHtml(p[1].trim())).join('<br>')
        return `<div style="display:flex;gap:2.5rem;margin:0.6em 0 0.9em 0"><div style="flex:1">${left}</div><div style="flex:1">${right}</div></div>`
      }
      let s = escapeHtml(para)
      if (trackChanges) {
        s = s
          .replace(/\[([^\][{}]*)\]/g, '<span style="color:#b4231a;text-decoration:line-through">$1</span>')
          .replace(/\{([^\][{}]*)\}/g, '<span style="color:#1a56b4;text-decoration:underline">$1</span>')
      }
      return `<p style="margin:0 0 0.9em 0">${s.replace(/\n/g, '<br>')}</p>`
    }).join('')
  }, [text, trackChanges])
  return (
    <div
      className={`rounded-md bg-white p-4 text-black overflow-x-auto ${quiet ? 'leading-[1.7]' : 'leading-relaxed'}`}
      // UXT-2: body size follows --me-font-size-ground-1 (KEN 15px); MOVU keeps
      // its old px via the fallback.
      style={{ fontSize: quiet ? 'var(--me-font-size-ground-1, 16px)' : 'var(--me-font-size-ground-1, 13px)' }}
      // Built from HTML-escaped Body text + our own span/p tags only.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function PdfBody({ url, pdfHeight }: { url: string; pdfHeight: number | string }) {
  return (
    <embed
      src={url}
      type="application/pdf"
      className="block w-full rounded-md border me-border"
      style={{ height: pdfHeight }}
    />
  )
}

function DocxBody({ url }: { url: string }) {
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (html !== null) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const r = await fetch(url, { credentials: 'include' })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const buf = await r.arrayBuffer()
        const mammoth = await import('mammoth')
        const { value } = await mammoth.convertToHtml({ arrayBuffer: buf })
        if (cancelled) return
        const sanitized = DOMPurify.sanitize(value, {
          FORBID_TAGS: ['script', 'object', 'embed', 'iframe', 'form', 'input', 'button'],
          FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
        })
        setHtml(sanitized)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'render failed')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [url, html])

  if (loading) return <div className="text-[12px] me-text-mute italic">Rendering .docx…</div>
  if (error) return (
    <div className="text-[12px] me-text-mute italic">
      Couldn't render: {error}.{' '}
      <a href={url} target="_blank" rel="noreferrer" className="underline">Download</a>.
    </div>
  )
  if (!html) return <div className="text-[12px] me-text-mute italic">Loading…</div>
  return (
    <div
      className="rounded-md bg-white p-4 leading-relaxed text-black overflow-x-auto"
      // UXT-2: body size follows --me-font-size-ground-1 (KEN 15px); MOVU keeps 13px via fallback.
      style={{ fontSize: 'var(--me-font-size-ground-1, 13px)' }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
